import Axios from "../redux/axiosInstance";
import { BASE_URL } from "./baseURL";
import { notificationsApiEndpoints } from "./index";

/**
 * Client for v1.0/Notifications — the recipient side only: register this
 * device, unregister it, and read the inbox.
 *
 * Sending lives elsewhere. `/send`, `/broadcast` and `/broadcast-service` are
 * driven by the admin dashboard and backend jobs; this portal is purely a
 * recipient, so those are deliberately not implemented here.
 *
 * Everything goes through the shared `Axios` instance, which attaches the
 * bearer token from localStorage and clears the session on a 401. All three
 * endpoints derive the user from that JWT, so firing them while signed out
 * yields a 401 rather than an anonymous registration.
 *
 * Note that /mine is a POST despite being a read — that is the API's shape, not
 * an oversight here.
 */

// Is the Notifications backend live?
//
// The portal ships ahead of it. Every automatic path here already fails open —
// device registration swallows its errors and retries on the next launch, and
// the push toggle reads the OS permission rather than the server — so the one
// thing a dead backend can still put in front of a customer is the inbox
// panel's "Couldn't load notifications" box, every single time they open it.
//
// While this is false a failed read is treated as an empty inbox: the panel
// shows its normal empty state, the reason goes to the console, and nobody is
// asked to retry something that cannot succeed yet. A customer is here to buy
// a policy or file a claim, and a feature they never asked for does not get to
// interrupt them with its own outage.
//
// Flip to true when /mine is live, and the panel reports its own failures
// again — including the Try again button, which will then do something.
export const NOTIFICATIONS_API_LIVE = false;

// Nothing here may end a session.
//
// The shared Axios instance clears local storage on any 401, which is right for
// a call the customer is waiting on and wrong for these: the inbox is read on
// sign-in, on every app resume and on every panel open, and none of it was
// asked for. A route that is not deployed yet answering 401 rather than 404
// would sign the customer out the instant they signed in, over and over. Even
// once it is live, an expired token is something the request the customer is
// actually making should discover — this one only listens for that (see
// onSessionExpired in hooks/usePushNotifications).
const AMBIENT_REQUEST = { skipUnauthorizedRedirect: true };

// Normalizes whatever the app calls a language into the two values the backend
// buckets on. Server-side anything starting with "ar" is Arabic and everything
// else (including null) is English, so this only has to avoid sending garbage.
export const toApiLocale = (language) =>
  String(language || "en").toLowerCase().startsWith("ar") ? "ar" : "en";

/**
 * Registers or refreshes this device's FCM token and subscribes it to its
 * topics.
 *
 * Every registered token ends up on exactly three topics — all subscribed by
 * the backend inside this call, never by this client:
 *
 *   {BroadcastTopic}            e.g. all-users / all-users-dev
 *                               everyone, regardless of language
 *   {BroadcastTopic}-{locale}   e.g. all-users-en / all-users-ar
 *                               one of the pair, never both
 *   cpr-{CPR}                   e.g. cpr-930101234
 *                               every device belonging to one customer
 *
 * Because a device sits on exactly one locale topic, no device receives a
 * broadcast twice. Re-registering with a different `Locale` is what moves it
 * between -en and -ar; there is no other lever.
 *
 * The CPR topic is derived server-side from the JWT's user — the client neither
 * sends nor names it. It exists for callers that know only a CPR and cannot do
 * a device-token lookup. Note the backend's own caveat: FCM client SDKs can
 * subscribe to any topic name and a CPR is not secret, so anything personal is
 * expected to go through /send rather than that topic.
 *
 * Idempotent — upserts on (UserId, Token) — so calling it on every launch, on
 * every token rotation and after every language switch is the intended usage.
 *
 * The three `subscribedTo*Topic` flags come back false when Firebase is
 * unconfigured or DryRun is on. That is expected in Development and is not a
 * failure: the token row is still persisted and `/send` still reaches the
 * device, because targeted sends address device tokens directly and never
 * involve a topic. Only topic-addressed pushes depend on membership.
 *
 * `cprTopic` is null when the signed-in user has no CPR on their AspNetUsers
 * row — the device is still registered and /send still works.
 *
 * @returns {Promise<{registered: boolean, broadcastTopic: string,
 *   localeTopic: string, cprTopic: string|null, locale: string,
 *   subscribedToBroadcastTopic: boolean, subscribedToLocaleTopic: boolean,
 *   subscribedToCprTopic: boolean}>}
 */
export const registerDevice = ({ token, platform = "web", locale }) =>
  Axios.post(
    notificationsApiEndpoints.registerDevice,
    {
      Token: token,
      Platform: platform,
      Locale: toApiLocale(locale),
    },
    AMBIENT_REQUEST
  ).then((res) => res.data);

/**
 * Removes the token and unsubscribes both of its topics.
 *
 * Resolves with `{ removed: false }` rather than rejecting when the token was
 * not on file, so logout does not need to care whether registration succeeded.
 *
 * Deliberately NOT on the axios instance, for three reasons — all of which come
 * from this only ever being called while the session is being torn down:
 *
 *  - `keepalive` lets the request outlive the page. Session expiry ends in
 *    `window.location.href = "/"`, which cancels an in-flight XHR; without this
 *    the device would stay registered to the user who just got logged out.
 *  - The JWT is passed in and pinned to the request. The axios interceptor
 *    reads it from localStorage in a microtask, i.e. *after* the synchronous
 *    logout has already cleared storage.
 *  - A 401 here must not re-enter the axios 401 handler, which would clear the
 *    session and redirect again.
 */
export const unregisterDevice = ({ token, authToken }) => {
  const url = `${BASE_URL.replace(/\/$/, "")}${
    notificationsApiEndpoints.unregisterDevice
  }`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  return fetch(url, {
    method: "POST",
    keepalive: true,
    headers,
    body: JSON.stringify({ Token: token }),
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`unregister-device failed with HTTP ${response.status}`);
    }
    return response.json().catch(() => null);
  });
};

/**
 * The signed-in user's inbox: their targeted notifications plus every broadcast
 * (rows with a null UserId), newest first.
 *
 * @param {number} [take] rows to return. Server defaults to 50 and caps at 200.
 * @returns {Promise<{count: number, notifications: Array}>}
 */
export const fetchMyNotifications = ({ take } = {}) =>
  Axios.post(
    notificationsApiEndpoints.mine,
    take ? { Take: take } : {},
    AMBIENT_REQUEST
  ).then((res) => res.data);
